// ============================================================================
// School (tenant) branding — the identity shown to staff/parents/students:
// header logo + name, login page, browser tab title. This is intentionally
// separate from the "EduSphere" PRODUCT brand (the software vendor), which
// stays fixed as a small "Powered by" credit — see components/layout/
// PoweredByFooter.tsx.
//
// Reads from the backend `schools` table (via /public/branding, no auth
// required) so it works pre-login too. Falls back to a generic product
// default when no school data exists yet, so every consumer stays safe to
// render for a brand-new/empty tenant without special-casing.
// ============================================================================

import { useQuery } from '@tanstack/react-query';
import { brandingApi } from '@/data/api';

/**
 * The ERP PRODUCT brand — distinct from the school (tenant) identity. Shown
 * centered in the app header. `Suvadhi` (Tamil: சுவடி, the palm-leaf primer on
 * which Tamil children first learned to read and write) is a coined,
 * verified-unused brand word; the Latin form doubles as the loading/tab fallback.
 */
export const PRODUCT_NAME = 'Suvadhi';
export const PRODUCT_NAME_TAMIL = 'சுவடி';
export const PRODUCT_TAGLINE = 'Enterprise School ERP';

export interface SchoolBranding {
  schoolId: string;
  schoolName: string;
  schoolNameTamil?: string | null;
  code?: string | null;
  affiliationNumber?: string | null;
  tagline: string | null;
  logoUrl: string | null;
  address: string | null;
  city: string | null;
  district?: string | null;
  state: string | null;
  pincode: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  establishedYear: number | null;
  board: string | null;
}

// Derive the API's origin (scheme + host [+ port], no "/api/v1" suffix) so a
// relative asset path returned by the backend (e.g.
// "/uploads/branding/logo.png") resolves against the API server regardless
// of where the SPA itself is served from.
const API_ORIGIN = (import.meta.env.VITE_API_URL || '').replace(/\/api\/v\d+\/?$/, '');

export function resolveBrandingAssetUrl(path?: string | null): string | undefined {
  if (!path) return undefined;
  if (/^(https?:)?\/\//i.test(path) || path.startsWith('data:')) return path;
  return `${API_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Hook for the school's tenant branding. Always returns a renderable
 * `schoolName`/`logoUrl` — falling back to the generic EduSphere product
 * name (never blank) while the request is loading or if it fails.
 */
export function useSchoolBranding() {
  const query = useQuery({
    queryKey: ['branding', 'public'],
    queryFn: async () => {
      const res = await brandingApi.getPublicBranding();
      if (!res?.success || !res.data) {
        throw new Error(res?.error || 'Failed to load school branding');
      }
      return res.data as SchoolBranding;
    },
    staleTime: 30 * 60 * 1000, // 30 min — branding changes rarely
    gcTime: 60 * 60 * 1000,
    retry: 1,
  });

  const branding = query.data;

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    branding,
    schoolName: branding?.schoolName || PRODUCT_NAME,
    tagline: branding?.tagline || PRODUCT_TAGLINE,
    logoUrl: resolveBrandingAssetUrl(branding?.logoUrl),
    board: branding?.board || null,
    establishedYear: branding?.establishedYear ?? null,
  };
}
